import React from 'react';
import { Facebook, Instagram } from 'lucide-react';

const TikTok = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FACEBOOK_PAGE = 'https://www.facebook.com/people/Aquify-Filtration-Partners/61583922105792/';
const INSTAGRAM_PAGE = 'https://www.instagram.com/aquify.filtration/';
const TIKTOK_PAGE = 'https://www.tiktok.com/@aquify.filtration';

// NOTE: Any black padding visible inside the iframe is controlled entirely by Facebook's
// video player — we cannot remove it from our side. The only way to eliminate it fully
// would be to host the video files directly or use custom thumbnail cards instead of
// Facebook iframe embeds.

const reels = [
  {
    embedSrc:
      'https://www.facebook.com/plugins/video.php?height=580&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1320227606878787%2F&show_text=false&width=325&t=0',
    reelUrl: 'https://www.facebook.com/reel/1320227606878787/',
    title: 'Aquify Facebook Reel 1',
    label: 'Reel 1',
  },
  {
    embedSrc:
      'https://www.facebook.com/plugins/video.php?height=580&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1615556913065794%2F&show_text=false&width=325&t=0',
    reelUrl: 'https://www.facebook.com/reel/1615556913065794/',
    title: 'Aquify Facebook Reel 2',
    label: 'Reel 2',
  },
];

const ReelCard = ({ reel }) => (
  <div className="flex flex-col items-center">
    {/* Portrait iframe card — centered, no stretched black bars */}
    <div
      className="rounded-2xl overflow-hidden shadow-lg border border-gray-200"
      style={{ width: '325px', maxWidth: '100%' }}
    >
      <iframe
        title={reel.title}
        src={reel.embedSrc}
        width="325"
        height="580"
        style={{ border: 'none', overflow: 'hidden', display: 'block' }}
        scrolling="no"
        frameBorder="0"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
      />
    </div>

    {/* Fallback link */}
    <a
      href={reel.reelUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200"
      style={{ color: '#1571BA' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = '#0C4288')}
      onMouseLeave={(e) => (e.currentTarget.style.color = '#1571BA')}
    >
      <Facebook className="h-4 w-4" />
      Open on Facebook
    </a>
  </div>
);

export const SocialSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div
            className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-5 max-w-full"
            style={{ border: '1px solid #50BAED' }}
          >
            <div className="flex gap-1.5 items-center">
              <Facebook className="h-4 w-4" style={{ color: '#1571BA' }} />
              <Instagram className="h-4 w-4" style={{ color: '#1571BA' }} />
              <TikTok className="h-4 w-4" style={{ color: '#1571BA' }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: '#0C4288' }}>
              Follow Aquify
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            See Aquify <span style={{ color: '#1571BA' }}>in Action</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch our latest videos, water treatment tips, and system showcases from the field.
          </p>
        </div>

        {/* 3-column layout: reel | blurb | reel (desktop) / stacked (mobile) */}
        <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-10 mb-12">

          {/* Left Reel */}
          <ReelCard reel={reels[0]} />

          {/* Center Blurb */}
          <div className="flex-1 flex flex-col items-center justify-center text-center max-w-xs md:pt-20">
            {/* Aquify watermark icon */}
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-5 shadow"
              style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
            >
              <Facebook className="h-7 w-7 text-white" />
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Follow Aquify on Social Media
            </h3>

            <p className="text-slate-700 text-base leading-relaxed mb-6">
              Follow Aquify on Facebook, Instagram, and TikTok for water treatment tips, project highlights, service updates, and behind-the-scenes content.
            </p>

            <div className="flex flex-col gap-3 w-full">
              <a
                href={FACEBOOK_PAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="aq-primary-btn w-full flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-xl transform hover:scale-105 shadow-md text-sm"
              >
                <Facebook className="h-5 w-5" />
                Follow on Facebook
              </a>
              <a
                href={INSTAGRAM_PAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="aq-primary-btn w-full flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-xl transform hover:scale-105 shadow-md text-sm"
              >
                <Instagram className="h-5 w-5" />
                Follow on Instagram
              </a>
              <a
                href={TIKTOK_PAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="aq-primary-btn w-full flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-xl transform hover:scale-105 shadow-md text-sm"
              >
                <TikTok className="h-5 w-5" />
                Follow on TikTok
              </a>
            </div>
          </div>

          {/* Right Reel */}
          <ReelCard reel={reels[1]} />

        </div>

      </div>
    </section>
  );
};

export default SocialSection;
